import { ViewHook } from 'phoenix_live_view';

import MentionComponent, { MentionProps } from '../components/mentions/Mention';
import ReactRenderer from '../react/renderer';
import MentionsProvider from '../services/mentions-provider';
import { sendError } from '../utils/errors';
import { buildPropsHelper, PropsHelper } from './helpers/props';

type RenderMentionHookProps = {
  value: string | undefined;
  slug: string;
  label: string;
  isClearable: boolean;
  clearEventName: string | undefined;
  clearEventTarget: string | undefined;
  mentionSuggestions: string;
};

type RenderMentionHook = Partial<ViewHook> &
  PropsHelper<RenderMentionHookProps> & {
    el: HTMLElement;
    renderer: ReactRenderer<any, MentionProps> | undefined;

    renderMention: () => void;
    attachRenderedMention: () => void;
  };

export default {
  RenderMention: {
    ...buildPropsHelper(),

    mounted() {
      this.setProps();
      this.renderMention();
      this.attachRenderedMention();
    },

    updated() {
      if (this.propsChanged()) {
        this.setProps();
        this.renderMention();
      }

      this.attachRenderedMention();
    },

    destroyed() {
      if (this.renderer) {
        this.renderer.destroy();
      }
    },

    getProps(): RenderMentionHookProps {
      return {
        value: this.el.dataset.value ?? undefined,
        slug: this.el.dataset.slug ?? '',
        label: this.el.dataset.label ?? '',
        isClearable: this.el.dataset.clearable === 'true',
        clearEventName: this.el.dataset.clearEventName || undefined,
        clearEventTarget: this.el.dataset.clearEventTarget || undefined,
        mentionSuggestions: this.el.dataset.mentionSuggestions || '{}',
      };
    },

    renderMention() {
      let mentionSuggestions;

      try {
        mentionSuggestions = this.el.dataset.mentionSuggestions ? JSON.parse(this.el.dataset.mentionSuggestions) : {};
      } catch (e: any) {
        sendError(e, {
          action: 'RenderMention#renderMention',
          tags: {
            component: 'RenderMention',
          },
          params: {
            mention_slug: this.slug,
            is_clearable: this.isClearable,
            clear_event_name: this.clearEventName,
            clear_event_target: this.clearEventTarget,
            mention_suggestions_length: this.el.dataset.mentionSuggestions?.length,
          },
          breadcrumb: {
            category: 'mentions',
            action: 'render-mention-parse-failed',
            metadata: {
              mention_slug: this.slug,
            },
          },
        });
      }

      const mentionsProvider = new MentionsProvider(mentionSuggestions);
      const wrapper = document.createElement('div');

      const onClear = () => {
        if (!this.clearEventName) return;

        if (this.clearEventTarget) {
          this.pushEventTo!(this.clearEventTarget, this.clearEventName, { value: this.value });
        } else {
          this.pushEvent!(this.clearEventName, { value: this.value });
        }
      };

      const nextProps: MentionProps = {
        id: this.slug,
        label: this.label,
        mentionsProvider,
        wrapper: 'div',
        onClear: this.isClearable ? onClear : undefined,
      };

      if (this.renderer) {
        this.renderer.updateProps(nextProps);
      } else {
        this.renderer = new ReactRenderer<any, MentionProps>(MentionComponent, {
          props: nextProps,
          rootElement: wrapper,
          renderOnInit: true,
        });
      }
    },

    attachRenderedMention() {
      if (this.renderer && this.renderer.rootElement) {
        this.el.replaceChildren(this.renderer.rootElement);
      }
    },
  } as RenderMentionHook,
};
